import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { PHONE_TEL, PHONE_DISPLAY } from "../site";
import { SERVICES } from "../data/services";

const cardMotion = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const CardShell = ({ testId, className = "", index = 0, children }) => (
  <motion.div
    {...cardMotion}
    transition={{ type: "spring", stiffness: 100, damping: 20, delay: index * 0.08 }}
    data-testid={testId}
    className={`group rounded-3xl bg-zinc-100 border border-zinc-200 overflow-hidden hover:-translate-y-1 transition-transform duration-300 ${className}`}
  >
    {children}
  </motion.div>
);

const CardImage = ({ src, alt, className = "h-44 md:h-52" }) => (
  <div className={`overflow-hidden ${className}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
    />
  </div>
);

const CardBody = ({ title, slug, children }) => (
  <div className="p-6 md:p-8">
    <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight text-zinc-950">{title}</h3>
    <p className="mt-3 text-sm md:text-base leading-relaxed text-zinc-600">{children}</p>
    <Link
      to={`/services/${slug}`}
      data-testid={`service-learn-more-${slug}`}
      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
    >
      Learn more <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

const SmallBody = ({ title, slug, children }) => (
  <div className="p-6">
    <h3 className="font-heading text-xl font-medium tracking-tight text-zinc-950">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-zinc-600">{children}</p>
    <Link
      to={`/services/${slug}`}
      data-testid={`service-learn-more-${slug}`}
      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
    >
      Learn more <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

export const Services = () => {
  const by = (slug) => SERVICES.find((s) => s.slug === slug);

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...cardMotion} transition={{ type: "spring", stiffness: 100, damping: 20 }} className="mb-14 md:mb-20 max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">What we do</p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-zinc-950">
            Every lock problem, <span className="text-brand">solved on the spot.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8">
          <CardShell testId="service-card-lockouts" index={0} className="lg:col-span-7">
            <CardImage src={by("car-lockouts").img} alt={by("car-lockouts").alt} />
            <CardBody title="Car Lockouts" slug="car-lockouts">
              Keys locked inside? Non-destructive entry gets you back behind the
              wheel in minutes — no scratches, no damage, no drama. Our most
              common call, and the fastest one we solve.
            </CardBody>
          </CardShell>

          <CardShell testId="service-card-fob" index={1} className="lg:col-span-5">
            <CardImage src={by("key-fob-programming").img} alt={by("key-fob-programming").alt} />
            <CardBody title="Key Fob Programming" slug="key-fob-programming">
              New fobs and remotes programmed on-site for nearly every make —
              a fraction of dealership prices.
            </CardBody>
          </CardShell>

          <CardShell testId="service-card-residential" index={2} className="lg:col-span-5">
            <CardImage src={by("residential-lockouts").img} alt={by("residential-lockouts").alt} />
            <CardBody title="Residential Lockouts" slug="residential-lockouts">
              Locked out of your house or apartment? Fast, damage-free entry —
              plus rekeying and fresh locks if your keys are gone for good.
            </CardBody>
          </CardShell>

          <CardShell testId="service-card-transponder" index={3} className="lg:col-span-7">
            <CardImage src={by("transponder-smart-keys").img} alt={by("transponder-smart-keys").alt} />
            <CardBody title="Transponder & Smart Keys" slug="transponder-smart-keys">
              Chipped keys cut and coded to your vehicle's immobilizer on the
              spot. Lost every key? We originate new ones from scratch — no tow
              to the dealer required.
            </CardBody>
          </CardShell>

          <CardShell testId="service-card-extraction" index={4} className="lg:col-span-4">
            <CardImage src={by("broken-key-extraction").img} alt={by("broken-key-extraction").alt} className="h-32 md:h-36" />
            <SmallBody title="Broken Key Extraction" slug="broken-key-extraction">
              Snapped in the door or ignition — we extract the fragment and cut
              a fresh key on the spot.
            </SmallBody>
          </CardShell>

          <CardShell testId="service-card-spare" index={5} className="lg:col-span-4">
            <CardImage src={by("spare-duplicate-keys").img} alt={by("spare-duplicate-keys").alt} className="h-32 md:h-36" />
            <SmallBody title="Spare & Duplicate Keys" slug="spare-duplicate-keys">
              One key is one mistake away from a bad day. Get a spare cut
              before you need it.
            </SmallBody>
          </CardShell>

          <CardShell testId="service-card-emergency" index={6} className="lg:col-span-4 !bg-brand !border-brand">
            <div className="p-6 md:p-8 h-full flex flex-col justify-between gap-6">
              <div>
                <h3 className="font-heading text-xl font-medium tracking-tight text-white">24/7 Emergency Dispatch</h3>
                <p className="mt-3 text-sm leading-relaxed text-blue-100">
                  Middle of the night, middle of winter. We answer and we roll.
                </p>
              </div>
              <a
                href={PHONE_TEL}
                data-testid="service-emergency-call-link"
                className="inline-flex items-center gap-2 self-start rounded-full bg-white px-5 py-3 text-sm font-semibold text-brand hover:bg-blue-50 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-white"
              >
                <Phone className="h-4 w-4" />
                {PHONE_DISPLAY}
              </a>
            </div>
          </CardShell>
        </div>
      </div>
    </section>
  );
};
